/**
 * The retained text selection: what the user last selected, stamped with the
 * tab it was selected on. Retained on purpose — moving focus into the Go box
 * or the dock composer collapses the DOM selection, and a selection that
 * evaporated on the way to the thing that uses it would be useless. It is
 * invalidated by tab identity (see `useActiveContext`), never by collapse.
 */
import { create } from 'zustand';

/** Longest selection kept, in characters — enough for a few paragraphs. */
export const MAX_SELECTION_CHARS = 2000;

/** A retained selection and the tab key it belongs to. */
export interface RetainedSelection {
  text: string;
  /** `tabKey(focusedTab(...))` at capture time. */
  tabKey: string;
}

interface SelectionState {
  selection: RetainedSelection | null;
  /** Retain `text` for `tabKey`; blank text is ignored so nothing is lost to a stray click. */
  record: (text: string, tabKey: string) => void;
  /** Forget the retained selection. */
  clear: () => void;
}

/** Store for the single retained selection. */
export const useSelectionStore = create<SelectionState>((set, get) => ({
  selection: null,
  record: (text, tabKey) => {
    const trimmed = text.trim().slice(0, MAX_SELECTION_CHARS);
    if (!trimmed) return;
    // Every caret nudge fires `selectionchange`; an unchanged selection must not
    // wake every subscriber of the context object.
    const current = get().selection;
    if (current && current.text === trimmed && current.tabKey === tabKey) return;
    set({ selection: { text: trimmed, tabKey } });
  },
  clear: () => set({ selection: null }),
}));
